import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/date";
import { Check, Pencil, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { FranchiseeTrainerPicker, useActiveFranchisees } from "@/components/franchisee/FranchiseeTrainerPicker";

const fmt = (d) => { try { return formatDate(d); } catch { return d; } };

// Fallback view straight from the sales_handoff snapshot (before GET lands).
const fromHandoff = (h) => ({
  training_days_booked: !!h?.training_days_booked,
  sessions: (h?.training_day_dates || []).map((d) => {
    const s = (h?.training_day_sessions || []).find((x) => x?.date === d) || {};
    return { date: d, trainer_type: s.trainer_type || null, trainer_franchisee_id: s.trainer_franchisee_id || null,
      trainer_name: s.trainer_type === "hq" ? "Creative Mojo HQ" : null, location: s.date ? s.location : (h?.training_location || null) };
  }),
  training_updated_at: h?.training_updated_at, training_updated_by: h?.training_updated_by,
});

function SessionSummary({ s, i }) {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs" data-testid={`handoff-training-session-${i}`}>
      <span className="px-2 py-0.5 font-medium text-stone-700 bg-stone-100 border border-stone-200 rounded-full tabular-nums" data-testid={`handoff-training-date-${i}`}>{fmt(s.date)}</span>
      <span className={s.trainer_name ? "text-stone-800 font-medium" : "text-stone-400 italic"} data-testid={`handoff-training-trainer-${i}`}>
        {s.trainer_name || "Trainer not set"}
      </span>
      {s.location && <span className="text-stone-500" data-testid={`handoff-training-location-${i}`}>· {s.location}</span>}
    </li>
  );
}

function SessionEditorRow({ s, i, franchisees, onChange, onRemove }) {
  return (
    <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/60" data-testid={`training-edit-row-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={s.date} onChange={(e) => onChange({ date: e.target.value })} data-testid={`training-edit-date-${i}`}
          className="px-2 py-1 bg-white border border-stone-300 rounded text-xs focus:outline-none focus:border-stone-900" />
        <select value={s.trainer_type || ""} data-testid={`training-edit-trainer-type-${i}`}
          onChange={(e) => onChange({ trainer_type: e.target.value || null, trainer_franchisee_id: null, trainer_name: e.target.value === "hq" ? "Creative Mojo HQ" : null })}
          className="px-2 py-1 bg-white border border-stone-300 rounded text-xs focus:outline-none focus:border-stone-900">
          <option value="">Trainer not set</option>
          <option value="hq">HQ (Creative Mojo)</option>
          <option value="franchisee">A franchisee</option>
        </select>
        <input type="text" value={s.location || ""} onChange={(e) => onChange({ location: e.target.value })} placeholder="Location / venue"
          data-testid={`training-edit-location-${i}`}
          className="flex-1 min-w-[10rem] px-2 py-1 bg-white border border-stone-300 rounded text-xs focus:outline-none focus:border-stone-900" />
        <button type="button" onClick={onRemove} data-testid={`training-edit-remove-${i}`} aria-label="Remove date"
          className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {s.trainer_type === "franchisee" && (
        <FranchiseeTrainerPicker franchisees={franchisees} selectedId={s.trainer_franchisee_id} selectedName={s.trainer_name}
          onSelect={(id, name) => onChange({ trainer_franchisee_id: id, trainer_name: name })} testid={`training-edit-franchisee-${i}`} />
      )}
    </div>
  );
}

function validate(booked, rows) {
  const seen = new Set();
  for (const r of rows) {
    if (!r.date) return "Every training row needs a date.";
    if (seen.has(r.date)) return `${fmt(r.date)} is listed twice.`;
    seen.add(r.date);
    if (r.trainer_type === "franchisee" && !r.trainer_franchisee_id) return `Choose the franchisee trainer for ${fmt(r.date)}.`;
  }
  return "";
}

function SaveNotice({ notice }) {
  if (!notice) return null;
  const warn = notice.kind === "warn";
  const Icon = warn ? AlertTriangle : CheckCircle2;
  return (
    <div data-testid="training-save-notice"
      className={`mt-2 flex items-start gap-2 text-[11px] rounded-lg px-3 py-2 border ${warn ? "text-amber-900 bg-amber-50 border-amber-200" : "text-emerald-900 bg-emerald-50 border-emerald-200"}`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{notice.text}</span>
    </div>
  );
}

function noticeFor(res) {
  if (!res.contact_updated) return { kind: "warn", text: "Saved on this franchisee only — no linked contact was found, so the Discovery Portal and calendar were not updated." };
  const cs = res.calendar_sync;
  if (cs && !cs.ok) {
    const reasons = { google_not_connected: "Google Calendar is not connected", calendar_not_configured: "no calendar is configured" };
    const why = reasons[cs.skipped_reason] || cs.skipped_reason || (cs.errors || []).join("; ") || "unknown error";
    return { kind: "warn", text: `Booking saved, but Google Calendar was not updated: ${why}.` };
  }
  return { kind: "ok", text: "Training booking saved — Discovery Portal and calendar updated." };
}

export function TrainingDaysSection({ franchiseeId, handoff, onSaved }) {
  const [view, setView] = useState(() => fromHandoff(handoff));
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const franchisees = useActiveFranchisees(api, !!edit);

  useEffect(() => {
    let live = true;
    api.get(`/franchisees/${franchiseeId}/sales-handoff/training`).then(({ data }) => { if (live) setView(data); }).catch(() => {});
    return () => { live = false; };
  }, [franchiseeId]);

  const startEdit = () => {
    setError(""); setNotice(null);
    setEdit({ booked: view.training_days_booked, rows: view.sessions.map((s) => ({ ...s })) });
  };
  const patchRow = (i, p) => setEdit((e) => ({ ...e, rows: e.rows.map((r, j) => (j === i ? { ...r, ...p } : r)) }));
  const addRow = () => setEdit((e) => ({ ...e, rows: [...e.rows, { date: "", trainer_type: null, trainer_franchisee_id: null, location: e.rows[e.rows.length - 1]?.location || "" }] }));

  const save = async () => {
    const msg = validate(edit.booked, edit.rows);
    if (msg) { setError(msg); return; }
    setSaving(true); setError("");
    try {
      const { data } = await api.patch(`/franchisees/${franchiseeId}/sales-handoff/training`, {
        training_days_booked: edit.booked,
        sessions: edit.rows.map(({ date, trainer_type, trainer_franchisee_id, location }) => ({ date, trainer_type, trainer_franchisee_id, location })),
      });
      setView(data); setEdit(null); setNotice(noticeFor(data));
      onSaved?.(data.sales_handoff);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save the training booking.");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-start gap-3 py-2 md:col-span-2" data-testid="handoff-training-section">
      <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${view.training_days_booked ? "bg-stone-950 border-stone-950 text-[#dddd16]" : "bg-white border-stone-300 text-transparent"}`}
        aria-hidden="true" data-testid="sales-handoff-training-day-s-booked">
        {view.training_days_booked ? <Check className="w-3 h-3" /> : null}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-sm ${view.training_days_booked ? "text-stone-900" : "text-stone-400"}`}>Training day(s) booked</span>
          {!edit && (
            <button type="button" onClick={startEdit} data-testid="training-edit-open"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stone-600 hover:text-stone-950 border border-stone-300 hover:border-stone-950 rounded-full px-2.5 py-0.5 transition-colors">
              <Pencil className="w-3 h-3" /> Edit training days
            </button>
          )}
        </div>
        {!edit && view.sessions.length > 0 && (
          <ul className="mt-1.5 space-y-1" data-testid="handoff-training-sessions">
            {view.sessions.map((s, i) => <SessionSummary key={s.date} s={s} i={i} />)}
          </ul>
        )}
        {!edit && view.training_updated_at && (
          <div className="mt-1 text-[10px] text-stone-400" data-testid="handoff-training-updated">
            Training updated {fmt(view.training_updated_at)}{view.training_updated_by ? ` · ${view.training_updated_by}` : ""}
          </div>
        )}
        {edit && (
          <div className="mt-2 space-y-2" data-testid="training-editor">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={edit.booked} onChange={(e) => setEdit((x) => ({ ...x, booked: e.target.checked }))}
                data-testid="training-edit-booked" className="w-4 h-4 rounded border-stone-300 accent-stone-950" />
              Training day(s) booked
            </label>
            {edit.rows.map((r, i) => (
              <SessionEditorRow key={i} s={r} i={i} franchisees={franchisees} onChange={(p) => patchRow(i, p)}
                onRemove={() => setEdit((e) => ({ ...e, rows: e.rows.filter((_, j) => j !== i) }))} />
            ))}
            <button type="button" onClick={addRow} data-testid="training-edit-add"
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:text-stone-950 transition-colors">
              <Plus className="w-3 h-3" /> Add date
            </button>
            {error && <div className="text-xs text-red-700" data-testid="training-edit-error">{error}</div>}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={save} disabled={saving} data-testid="training-edit-save"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-950 text-[#dddd16] text-xs font-bold uppercase tracking-wider hover:bg-stone-800 disabled:opacity-50 transition-colors">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
              <button type="button" onClick={() => { setEdit(null); setError(""); }} disabled={saving} data-testid="training-edit-cancel"
                className="px-3 py-1.5 rounded-lg border border-stone-300 text-xs font-bold uppercase tracking-wider text-stone-700 hover:border-stone-950 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}
        <SaveNotice notice={notice} />
      </div>
    </div>
  );
}
